import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  ShoppingBag,
  ShoppingCart,
  Zap,
  Plus,
  Minus,
  Loader2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import SummaryApi from "../common";
import { useCart } from "../context/CartContext";
import toast from "react-hot-toast";
import packageStaticImg from "../assets/recomendation/package.png";

const RecommendedProducts = ({ answers, onReset, onClose }) => {
  const navigate = useNavigate();
  const { fetchCartCount } = useCart();
  const [dbProducts, setDbProducts] = useState([]);
  const [dbPackages, setDbPackages] = useState([]);
  const [cartData, setCartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const hasFetched = useRef(false);

  // --- IMAGE URL LOGIC ---
  const IMAGE_BASE_URL = SummaryApi?.productImage?.url
    ? SummaryApi.productImage.url.replace("/api/uploads", "")
    : "http://192.168.1.254:3001";

  // Helper to safely format image URLs and log them for debugging
  const getImageUrl = (path) => {
    if (!path) return null;

    // Ensure the path starts with a leading slash if it doesn't have one
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    const fullUrl = `${IMAGE_BASE_URL}${cleanPath}`;

    // DEBUG LOG: Check this in your browser console
    // console.log(`🖼️ Image Debug [${path.split("/").pop()}]:`, fullUrl);

    return fullUrl;
  };

  // Log the Base URL once on load
  // useEffect(() => {
  //   console.log("🛠️ Image Base URL configured as:", IMAGE_BASE_URL);
  // }, [IMAGE_BASE_URL]);

  // --- Data Fetching ---
  const getUserId = useCallback(() => {
    const data = localStorage.getItem("user");
    if (!data) return null;
    try {
      const parsed = JSON.parse(data);
      return parsed.user_id || parsed.id || null;
    } catch {
      return null;
    }
  }, []);

  const getGuestCart = useCallback(
    () => JSON.parse(sessionStorage.getItem("guest_cart") || "[]"),
    [],
  );
  const setGuestCart = useCallback(
    (cart) => sessionStorage.setItem("guest_cart", JSON.stringify(cart)),
    [],
  );
  const fetchHeaderCount = () => window.dispatchEvent(new Event("cartUpdate"));

  const fetchCurrentCart = useCallback(async () => {
    const userId = getUserId();
    if (userId) {
      try {
        const res = await fetch(SummaryApi.getCartById.url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId }),
        });
        const result = await res.json();
        if (result.status) setCartData(result.data);
      } catch (err) {
        console.error(err);
      }
    } else {
      setCartData(getGuestCart());
    }
  }, [getUserId, getGuestCart]);

  useEffect(() => {
    if (hasFetched.current) return;
    const fetchData = async () => {
      setLoading(true);
      hasFetched.current = true;
      try {
        const [prodRes, pkgRes] = await Promise.all([
          fetch(SummaryApi.getActiveProduct.url),
          fetch(SummaryApi.getActivePackage.url),
        ]);
        const prodData = await prodRes.json();
        const pkgData = await pkgRes.json();
        if (prodData.status) setDbProducts(prodData.data || []);
        if (pkgData.status) setDbPackages(pkgData.data || []);
        await fetchCurrentCart();
      } catch (error) {
        hasFetched.current = false;
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [fetchCurrentCart]);

  const getItemQty = (itemId, unitId) => {
    const found = cartData.find(
      (c) =>
        String(c.product_id) === String(itemId) &&
        String(c.product_unit_id) === String(unitId),
    );
    return found ? found.quantity : 0;
  };

  const handleAction = async (item, delta = 1) => {
    if (item.type === "package") {
      navigate("/packages");
      onClose();
      return;
    }
    const userId = getUserId();
    const unitId = item.units?.[0]?.product_unit_id || null;
    const currentQty = getItemQty(item.product_id, unitId);
    const newQty = currentQty + delta;
    if (newQty < 0) return;
    setIsSyncing(true);

    if (!userId) {
      let cart = getGuestCart();
      const idx = cart.findIndex(
        (i) =>
          String(i.product_id) === String(item.product_id) &&
          String(i.product_unit_id) === String(unitId),
      );
      if (newQty === 0) {
        if (idx > -1) cart.splice(idx, 1);
      } else if (idx > -1) {
        cart[idx].quantity = newQty;
      } else {
        cart.push({
          product_id: item.product_id,
          product_unit_id: unitId,
          quantity: 1,
          product_name: item.displayName,
          product_type: "normal product",
        });
      }
      setGuestCart(cart);
      setCartData(cart);
      await fetchCartCount();
      fetchHeaderCount();
      setIsSyncing(false);
      return;
    }

    try {
      const cartItem = cartData.find(
        (c) =>
          String(c.product_id) === String(item.product_id) &&
          String(c.product_unit_id) === String(unitId),
      );
      let res;
      if (newQty === 0) {
        res = await fetch(SummaryApi.cartDelete.url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cart_id: cartItem.cart_id }),
        });
      } else if (currentQty === 0) {
        res = await fetch(SummaryApi.addToCart.url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: userId,
            product_id: item.product_id,
            product_unit_id: unitId,
            quantity: 1,
          }),
        });
      } else {
        res = await fetch(SummaryApi.cartUpdate.url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: userId,
            product_id: item.product_id,
            product_unit_id: unitId,
            quantity: newQty,
            cart_id: cartItem.cart_id,
          }),
        });
      }
      if (res.ok) {
        await fetchCurrentCart();
        await fetchCartCount();
        fetchHeaderCount();
      }
    } catch (err) {
      toast.error("Error updating cart");
    } finally {
      setIsSyncing(false);
    }
  };
  const items = useMemo(() => {
    if (loading) return [];

    // 1. Normalize all answers (lowercase, trim, and handle strings safely)
    const allValues = Object.values(answers)
      .flat()
      .map((v) => String(v).toLowerCase().trim());

    // 2. Determine Duration (Question 1) with robust matching
    const durationValue = allValues.find((v) => v.includes("day"));
    let durationType = "";
    if (durationValue) {
      if (durationValue.includes("2-3")) durationType = "starter";
      else if (durationValue.includes("3-5")) durationType = "essential";
      else if (durationValue.includes("5-7")) durationType = "popular";
      else if (
        durationValue.includes("7") &&
        (durationValue.includes(">") ||
          durationValue.includes("greater") ||
          durationValue.includes("more"))
      ) {
        durationType = "premium";
      }
    }

    // 3. Define Symptom Groups based on your table
    const painSymptoms = [
      "cramps",
      "mood swings",
      "headache",
      "backache",
      "pain in legs",
    ];
    const leafSymptoms = ["bloating", "fatigue", "tender breast", "none"];

    const hasPain = allValues.some((s) => painSymptoms.includes(s));
    const needsLeafPads = allValues.some((s) => leafSymptoms.includes(s));

    let list = [];

    // Helper functions to format and find items
    const addToList = (item, type) => {
      if (!item) return;
      list.push({
        ...item,
        type: type,
        id: type === "package" ? item.package_id : item.product_id,
        displayName:
          type === "package" ? item.package_name : item.product_title,
        displayPrice: item.selling_price || item.price,
        displayDesc: type === "package" ? item.title : item.subtitle,
        displayImage: type === "package" 
      ? packageStaticImg 
      : getImageUrl(item.product_image1),
      });
    };

    const findPkg = (name) =>
      dbPackages.find((p) =>
        p.package_name?.toLowerCase().includes(name.toLowerCase()),
      );
    const findProd = (name) =>
      dbProducts.find((p) =>
        p.product_title?.toLowerCase().includes(name.toLowerCase()),
      );

    // --- LOGIC PER TABLE ---

    // A. Add Package based on Duration
    if (durationType === "starter") addToList(findPkg("Starter"), "package");
    if (durationType === "essential")
      addToList(findPkg("Essential"), "package");
    if (durationType === "popular") addToList(findPkg("Popular"), "package");
    if (durationType === "premium") addToList(findPkg("Premium"), "package");

    // B. Add Combo Packs & Active Flex for Pain (Per Table)
    if (hasPain) {
      addToList(findProd("Active flex"), "product");
      // If duration is short (Starter/Essential), give Combo 20. Otherwise, give Combo 40.
      const comboName =
        durationType === "starter" || durationType === "essential"
          ? "Combo Pack (20)"
          : "Combo Pack (40)";
      addToList(findPkg(comboName), "package");
    }

    // C. Add Leaf Pads (Per Table & your original 3-5 day logic)
    // Recommended if symptoms are Bloating/Fatigue/Tender/None OR if duration is 3-5 days
    if (needsLeafPads || durationType === "essential") {
      addToList(findProd("Leaf Pads"), "product");
    }

    // Final: Return unique items only
    return list.filter(
      (item, index, self) =>
        index ===
        self.findIndex((t) => t.id === item.id && t.type === item.type),
    );
  }, [loading, dbProducts, dbPackages, answers]);

  if (loading)
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Loader2 className="animate-spin text-pink-500 mb-2" size={32} />
        <p className="text-gray-500 font-medium">Analyzing results...</p>
      </div>
    );

  return (
    <div className="flex flex-col h-full max-h-[85vh]">
      <div className="text-center mb-6 shrink-0">
        <h2 className="text-2xl font-extrabold text-gray-900">
          Your Recommendations
        </h2>
      </div>
      <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {items.map((item) => {
            const qty =
              item.type === "product"
                ? getItemQty(item.product_id, item.units?.[0]?.product_unit_id)
                : 0;
            return (
              <div
                key={`${item.type}-${item.id}`}
                className="bg-white border border-gray-100 rounded-3xl p-4 shadow-sm flex flex-col justify-between hover:border-pink-200 transition-colors"
              >
                <div>
                  <div className="relative w-full h-32 mb-4 bg-gray-50 rounded-2xl overflow-hidden group">
                    {item.displayImage ? (
                      <img
                        src={item.displayImage}
                        alt={item.displayName}
                        className="w-full h-full object-contain mix-blend-multiply p-2 transform group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-pink-50">
                        <ShoppingBag className="text-pink-200" size={40} />
                      </div>
                    )}
                    <div className="absolute top-2 left-2 bg-white/80 backdrop-blur-md px-2 py-1 rounded-lg text-[10px] font-bold text-pink-600 uppercase shadow-sm">
                      {item.type}
                    </div>
                  </div>
                  <div className="flex flex-col mb-4">
                    <h4 className="font-bold text-gray-800 text-sm line-clamp-1 mb-1">
                      {item.displayName}
                    </h4>
                    <div className="flex items-center justify-between">
                      <span className="font-black text-pink-600 text-lg">
                        ₹{item.displayPrice}
                      </span>
                      <p className="text-[10px] text-gray-400 uppercase font-bold text-right leading-tight max-w-[100px]">
                        {item.displayDesc}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  {qty === 0 ? (
                    <button
                      onClick={() => handleAction(item, 1)}
                      disabled={isSyncing}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-gray-900 text-gray-900 font-bold text-[11px] disabled:opacity-50"
                    >
                      <ShoppingCart size={14} /> Add to Cart
                    </button>
                  ) : (
                    <div className="w-full bg-white h-10 rounded-xl flex items-center justify-between px-4 shadow-sm border border-rose-100">
                      <button
                        onClick={() => handleAction(item, -1)}
                        disabled={isSyncing}
                        className="text-rose-500 p-1 hover:bg-rose-50 rounded-lg disabled:opacity-30"
                      >
                        <Minus size={18} />
                      </button>
                      <span className="text-base font-black text-rose-950">
                        {qty}
                      </span>
                      <button
                        onClick={() => handleAction(item, 1)}
                        disabled={isSyncing}
                        className="text-rose-500 p-1 hover:bg-rose-50 rounded-lg disabled:opacity-30"
                      >
                        <Plus size={18} />
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() => {
                      if (item.type === "package") navigate("/packages");
                      else navigate(`/product/${window.btoa(item.product_id)}`);
                      onClose();
                    }}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gray-900 text-white font-bold text-[11px]"
                  >
                    <Zap
                      size={14}
                      className="fill-yellow-400 text-yellow-400"
                    />{" "}
                    View Details
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RecommendedProducts;
